import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { formatDateUTC } from "@plasma/utils/time";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

const deleteShiftRef = api.employeeShifts.deleteShift;

// V4.1: Route-based modal for deleting employee shifts
// Search params pass shift data to avoid an additional fetch
const searchParamsSchema = z.object({
  employeeName: z.string(),
  employeeImageUrl: z.string().optional(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  minutesWorked: z.coerce.number(),
  estimatedWage: z.coerce.number().optional(),
});

export const Route = createFileRoute(
  "/_authenticated/employees/shifts/$shiftId/delete"
)({
  component: DeleteShiftModal,
  validateSearch: searchParamsSchema,
});

function DeleteShiftModal() {
  const navigate = useNavigate();
  const { shiftId } = Route.useParams();
  const {
    employeeName,
    employeeImageUrl,
    date,
    startTime,
    endTime,
    minutesWorked,
    estimatedWage,
  } = Route.useSearch();

  const handleClose = () => {
    void navigate({ to: "/employees/shifts" });
  };

  const deleteShiftMutation = useMutation({
    mutationFn: ({
      employeeShiftId,
    }: {
      employeeShiftId: Id<"employeeShifts">;
    }) => {
      return convexClient.mutation(deleteShiftRef, {
        employeeShiftId,
      });
    },
    onSuccess: () => {
      handleClose();
    },
  });

  const handleDelete = async () => {
    if (deleteShiftMutation.isPending) {
      return;
    }
    await deleteShiftMutation.mutateAsync({
      employeeShiftId: toConvexTableId("employeeShifts", shiftId),
    });
    ProductEvents.shiftDeleted(shiftId);
  };

  return (
    <AlertDialog onOpenChange={(open: boolean) => !open && handleClose()} open>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Shift</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this employee's shift?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="bg-muted space-y-4 rounded-xl p-4">
          <div className="label text-foreground flex items-center gap-3">
            <img
              alt={`${employeeName}'s profile`}
              className="size-6 rounded-full"
              height={24}
              src={employeeImageUrl ?? "/images/avatar-placeholder.svg"}
              width={24}
            />
            {employeeName}
          </div>
          <div>
            {[
              {
                title: "Date",
                value: formatDateUTC(new Date(Number(date)), "M/D/YYYY"),
              },
              {
                title: "Start & end",
                value: `${timeConvert24hrTo12hr(startTime)} - ${timeConvert24hrTo12hr(endTime)}`,
              },
              {
                title: "Total hours",
                value: `${Math.floor(minutesWorked / 60)}h ${minutesWorked % 60}m`,
              },
              {
                title: "Estimated wage",
                value:
                  estimatedWage?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  }) ?? "-",
              },
            ].map(({ title, value }) => (
              <p
                className="body-sm text-muted-foreground flex items-center gap-3"
                key={title}
              >
                <span className="subtitle-sm text-foreground block w-32">
                  {title}
                </span>
                {value}
              </p>
            ))}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleteShiftMutation.isPending}
            onClick={handleDelete}
            variant="destructive"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
